import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from 'src/app/pipes/translate.pipe';
import { VisitService } from 'src/app/services/visit.service';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

const MY_DATE_FORMAT = {
  parse: {
    dateInput: 'dd.MM.yyyy'
  },
  display: {
    dateInput: 'dd.MM.yyyy',
    monthYearLabel: 'MMMM yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM yyyy'
  }
};

@Component({
  standalone: true,
  selector: 'app-start-modal',
  templateUrl: './start-modal.component.html',
  styleUrls: ['./start-modal.component.scss'],
  imports: [
    TranslatePipe,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    ReactiveFormsModule,
    CommonModule
  ],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMAT },
    { provide: MAT_DATE_LOCALE, useValue: 'de-DE' }
  ]
})
export class StartModalComponent implements OnInit {
  @Input() visitType: 'bto' | 'gfb' = 'bto';

  // Datepicker-FormControl mit Validatoren
  dateControl = new FormControl<Date | null>(new Date(), [
    Validators.required,
    this.futureDateValidator
  ]);

  date = '';

  constructor(
    public modal: NgbActiveModal,
    private visitService: VisitService,
    private router: Router,
    private dateAdapter: DateAdapter<Date>
  ) {}

  ngOnInit(): void {
	this.dateAdapter.setLocale('de'); 
    this.dateAdapter.getFirstDayOfWeek = () => 1; // beginnt Woche am Montag
  }

  // Validator: Datum max. 1 Jahr in der Zukunft
  futureDateValidator(control: import('@angular/forms').AbstractControl) {
    const value = control.value;
    if (!(value instanceof Date)) return null;

    const now = new Date();
    const oneYearAhead = new Date();
    oneYearAhead.setFullYear(now.getFullYear() + 1);

    if (value > oneYearAhead) {
      return { tooFar: true };
    }
    return null;
  }

  // UTC-Formatierung yyyy-LL-dd'T'HH:mm:ss.0000000'Z' aber ohne luxon
  private formatUtc(date: Date): string {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.0000000Z`;
  }

  leave() {
    this.modal.close(true);
  }

  async submit() {
    if (this.dateControl.invalid) {
      this.dateControl.markAsTouched();
      return;
    }

    const date = this.dateControl.value!;
    const utcDate = new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0, 0, 0
    ));

    this.date = this.formatUtc(utcDate);

    let result: any;
    if (this.visitType === 'bto') {
      result = await this.visitService.startVisit(this.date);
    } else if (this.visitType === 'gfb') {
      result = await this.visitService.startAssessment(this.date);
    }

    this.modal.close(true);

    if (result.status === 'StartRequested') {
      this.router.navigate(['/home']);
    } else if (result.status === 'Started') {
      this.visitService.clearVisit();
      location.reload();
    }
  }

  allowOnlyNumbersAndDots(event: KeyboardEvent): void {
	const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
	const regex = /[0-9.]/;

	if (allowedKeys.includes(event.key)) {
		return; // diese Tasten immer erlauben
	}

	if (!regex.test(event.key)) {
		event.preventDefault(); // blockiert alles außer Zahlen und Punkt
	}
}

}
